import React, { useContext } from 'react';
import { Common } from '../../static/Messages';
import DataCard from '../dataCard/DataCard';
import ApiKey from '@shared/api/dto/ApiKey';
import { Button, CardDivider, StatusMessage, Tooltip, TooltipContainer, TooltipTarget } from 'is24-corecss';
import reportingApi from '../../api/ReportingApi';
import { MessageContext } from '../../locale/MessageProvider';

type ApiKeyDetailsProps = {
  apiKeys: ApiKey[];
  onDelete: (apiKey: ApiKey) => void;
  onEdit: () => void;
  className?: string;
};

enum fieldName {
  title,
  productionSystem,
  sandboxSystem,
  tooltipNoReport,
  tooltipSandboxSystem,
  apiCallsLabel,
  previousMonthCalls,
  currentMonthCalls,
  deleteApiKey,
}

const ApiKeyDetails: React.FC<ApiKeyDetailsProps> = ({ apiKeys, onDelete, onEdit, className }: ApiKeyDetailsProps) => {
  const messageFormatter = useContext(MessageContext);
  const PRO = process.env.STAGE === 'pro';

  const getPdf = (apiKey: ApiKey) => () => {
    reportingApi.fetch(apiKey.apiKey).then((data) => {
      if (data) {
        const file = new Blob([data], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        const fileLink = document.createElement('a');
        fileLink.href = fileURL;

        const date = new Date();
        fileLink.download = 'API_usage_report___' + apiKey.apiKey + '__' + date.getFullYear() + '_' + date.getMonth();
        fileLink.click();
      }
    });
  };

  return (
    <DataCard
      title={messageFormatter.formatMessage(ApiKeyDetails, fieldName[fieldName.title])}
      className={className}
      button={
        PRO ? (
          <Button
            data-testid="data-card-button-api-key-details"
            iconStandalone={true}
            aria-label="Edit"
            size="small"
            appearance="tertiary"
            onClick={onEdit}
          >
            <span className="is24-icon-plus font-bold font-info"></span>
          </Button>
        ) : (
          <span></span>
        )
      }
    >
      {!apiKeys.length ? (
        <div data-testid="no-api-key">
          <StatusMessage
            headline={messageFormatter.formatCommonMessage(Common.notice)}
            message={messageFormatter.formatCommonMessage(Common.noApiKey)}
            statusType="info"
          />
        </div>
      ) : (
        <div className="grid grid-flex">
          {apiKeys.map((apiKey, index) => (
            <div key={index} className="grid-item one-whole">
              <div className="grid grid-flex">
                <div className="grid-item one-whole padding-top-m">
                  <div className="grid grid-flex grid-justify-space-between">
                    <span className="font-bold">{apiKey.apiKey}</span>
                    <div className="indicator indicator--tag">
                      {apiKey.permissions?.length && (
                        <TooltipContainer data-testid="scopes-tooltip" className="grid-item">
                          <TooltipTarget className="is24-icon-info font-lightgray font-l vertical-center" />
                          <Tooltip bodyPosition="bottom" arrowPosition="right">
                            <div className="font-white" style={{ width: 350 }}>
                              <ul>
                                {apiKey.permissions?.map((operation, opIndex) => (
                                  <li key={opIndex}>
                                    <span style={{ whiteSpace: 'normal' }}>{'- ' + operation}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </Tooltip>
                        </TooltipContainer>
                      )}
                      <span className="margin-left-s">
                        {apiKey.production
                          ? messageFormatter.formatMessage(ApiKeyDetails, fieldName[fieldName.productionSystem])
                          : messageFormatter.formatMessage(ApiKeyDetails, fieldName[fieldName.sandboxSystem])}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-flex grid-justify-end">
                    <span>{messageFormatter.formatCommonMessage(Common.from) + ' ' + apiKey.dateOfCreation}</span>
                  </div>
                  {!apiKey.production && (
                    <span className="grid grid-flex grid-justify-end">
                      <Button
                        data-testid="delete-apiKey"
                        appearance="secondary"
                        size="small"
                        iconStandalone={true}
                        aria-label="Edit"
                        onClick={() => onDelete(apiKey)}
                      >
                        {messageFormatter.formatMessage(ApiKeyDetails, fieldName[fieldName.deleteApiKey])}
                      </Button>
                    </span>
                  )}
                </div>
                <div className="grid-item one-whole padding-vertical-m">
                  <div className="grid grid-flex grid-justify-space-between">
                    <div className="grid-item grid-flex one-whole" style={{ alignItems: 'center' }}>
                      <div className="grid grid-flex one-third float-left">
                        <Button
                          data-testid="download-pdf"
                          appearance="primary"
                          size="small"
                          iconStandalone={true}
                          aria-label="Edit"
                          disabled={!apiKey.reportIsAvailable || !apiKey.production}
                          onClick={getPdf(apiKey)}
                        >
                          <span className="grid-item is24-icon-download">
                            <span
                              className="padding-left-s"
                              style={{
                                fontFamily: 'Make It Sans IS24 Web,Verdana,DejaVu Sans,Arial,Helvetica,sans-serif',
                                fontWeight: 600,
                              }}
                            >
                              API-Calls Report
                            </span>
                          </span>
                        </Button>
                        {(!apiKey.reportIsAvailable || !apiKey.production) && (
                          <TooltipContainer data-testid="pdf-report-tooltip" className="grid-item margin-left-s">
                            <TooltipTarget className="is24-icon-info font-lightgray font-l vertical-center" />
                            <Tooltip bodyPosition="bottom" arrowPosition="right">
                              <div className="font-white" style={{ width: 250 }}>
                                {!apiKey.reportIsAvailable && apiKey.production
                                  ? messageFormatter.formatMessage(ApiKeyDetails, fieldName[fieldName.tooltipNoReport])
                                  : messageFormatter.formatMessage(
                                      ApiKeyDetails,
                                      fieldName[fieldName.tooltipSandboxSystem],
                                    )}
                              </div>
                            </Tooltip>
                          </TooltipContainer>
                        )}
                      </div>
                      {apiKey.production && (
                        <span className="grid-item two-third float-right align-text-top">
                          <div className="grid-item one-third align-center">
                            {messageFormatter.formatMessage(ApiKeyDetails, fieldName[fieldName.apiCallsLabel])}
                          </div>
                          <div className="grid-item one-third">
                            <span className="grid-item align-center">
                              {messageFormatter.formatMessage(ApiKeyDetails, fieldName[fieldName.previousMonthCalls])}
                            </span>
                            <span className="grid-item align-center font-bold">
                              {apiKey.previousMonthCalls !== null ? apiKey.previousMonthCalls : '-'}
                            </span>
                          </div>
                          <div className="grid-item one-third">
                            <span className="grid-item align-center">
                              {messageFormatter.formatMessage(ApiKeyDetails, fieldName[fieldName.currentMonthCalls])}
                            </span>
                            <span className="grid-item align-center font-bold">
                              {apiKey.currentMonthCalls !== null ? apiKey.currentMonthCalls : '-'}
                            </span>
                          </div>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <CardDivider />
            </div>
          ))}
        </div>
      )}
    </DataCard>
  );
};

export default ApiKeyDetails;
